/**
 * League records, computed rather than remembered.
 *
 * Twenty-three seasons, 1,770 matchups, 3,756 draft picks. Every December somebody re-derives
 * "biggest blowout ever" by scrolling Yahoo, gets a different answer than last time, and the
 * league moves on. This computes each record once from the scraped data and shows its working —
 * every entry carries the season and the opponent, so it can be argued with.
 *
 * Two honesty rules, both load-bearing:
 *
 *   Records are computed ONLY over the seasons that have the data they need. A career win total
 *   that silently omits 2003-2008 because those seasons have no manager names is worse than one
 *   that says which years it covers. Every record carries its own coverage.
 *
 *   Nothing is inferred across a gap. 2003-2009 postseason games are not split into championship
 *   and consolation in the source, so "playoff record" simply does not count them.
 */

import { gmFor, identityFor, managerShort, pairingVersion } from './identity.js';
import matchupData from '../data/matchup-history.json';
import standingsData from '../data/standings-history.json';
import keeperData from '../data/keeper-history.json';
import draftData from '../data/draft-history.json';

/* -- shapes ---------------------------------------------------------------------------------- */

interface Side { gm: string | null; team: string; points: number; gmResolved?: boolean }
export interface Matchup {
  week: number; kind?: string; home: Side; away: Side; winner: string | null;
}
interface MatchupSeason {
  season: number; regularSeasonWeeks: number; playoffWeeks?: number[];
  postseasonKindResolved?: boolean; matchups: Matchup[];
}
interface StandingRow {
  rank: number; team: string; gm: string | null; w: number; l: number; t: number;
  pf: number; pa: number; playoff: boolean; gmResolved?: boolean;
}
interface StandingSeason {
  season: number; teams: number; standings: StandingRow[];
  champion?: string; runnerUp?: string; thirdPlace?: string;
}

const MATCHUPS = (matchupData as unknown as { seasons: MatchupSeason[] }).seasons;
const STANDINGS = (standingsData as unknown as { seasons: StandingSeason[] }).seasons;

/* -- scope ----------------------------------------------------------------------------------- */

/**
 * Which slice of history a board is about.
 *
 * "All-time" is the honest default and the wrong answer to most arguments. Nobody in the room
 * cares that a manager who left in 2012 has a better win rate than they do, and a career table
 * that stops at 2008 makes "who is best right now" unanswerable. So every board takes a scope:
 * a season filter and a manager filter, both plain predicates.
 *
 * Filtering never changes how a number is COMPUTED, only which rows go into it — a win percentage
 * inside a scope is still wins over games, just over fewer games. Coverage is reported for the
 * scope, so a board always says which years it is actually talking about.
 */
export interface Scope {
  season?: (year: number) => boolean;
  gm?: (id: string) => boolean;
}

const ALL: Required<Scope> = { season: () => true, gm: () => true };
const withScope = (s: Scope = {}): Required<Scope> => ({ ...ALL, ...s });

/* -- coverage -------------------------------------------------------------------------------- */

export interface Coverage {
  seasons: number[];
  from: number;
  to: number;
  /** Seasons deliberately left out, and why. */
  excluded: Array<{ season: number; why: string }>;
}

function coverageOf(seasons: number[], excluded: Array<{ season: number; why: string }> = []): Coverage {
  const sorted = [...seasons].sort((a, b) => a - b);
  return { seasons: sorted, from: sorted[0] ?? 0, to: sorted[sorted.length - 1] ?? 0, excluded };
}

/**
 * Seasons where every team has a manager — the only ones a career record can use.
 *
 * A FUNCTION rather than a constant, because the commissioner's pairing tool can widen this at
 * runtime: pairing up 2008's ten team names brings 2008 into every board below, and un-pairing
 * one takes it straight back out. Nothing here is baked at import time.
 */
export function namedSeasons(): number[] {
  return MATCHUPS
    .filter((s) => s.matchups.every((m) => sideGm(s.season, m.home) && sideGm(s.season, m.away)))
    .map((s) => s.season);
}

function unnamed(): Array<{ season: number; why: string }> {
  const ok = new Set(namedSeasons());
  return MATCHUPS.filter((s) => !ok.has(s.season)).map((s) => ({
    season: s.season,
    why: 'Yahoo hides manager names for this season, and it has not been paired up yet',
  }));
}

const sideGm = (season: number, side: Side): string | null => gmFor(season, side);

/* -- single-game records --------------------------------------------------------------------- */

export interface GameRecord {
  gm: string;
  season: number;
  week: number;
  points: number;
  /** His team that year. Kept because it is the key the box scores are filed under. */
  team: string;
  /** The opponent's manager where the scrape resolved one, otherwise his team name. */
  opponent: string;
  /**
   * Whether that opponent is a MANAGER or just a team name. Sniffing the string is not good
   * enough — "'roid rage" looks exactly like a person to a regex, and treating it as one made the
   * head-to-head grid asymmetric.
   */
  opponentResolved: boolean;
  opponentPoints: number;
  kind?: string;
}

function allGames(): GameRecord[] {
  const out: GameRecord[] = [];
  for (const s of MATCHUPS) {
    for (const m of s.matchups) {
      for (const [me, them] of [[m.home, m.away], [m.away, m.home]] as const) {
        const mine = sideGm(s.season, me);
        if (!mine) continue;
        const theirs = sideGm(s.season, them);
        out.push({
          gm: mine,
          season: s.season,
          week: m.week,
          points: me.points,
          team: me.team,
          opponent: theirs ?? them.team,
          opponentResolved: Boolean(theirs),
          opponentPoints: them.points,
          ...(m.kind ? { kind: m.kind } : {}),
        });
      }
    }
  }
  return out;
}

/**
 * Derived tables are cached against the pairing version rather than computed at import: 1,770
 * matchups is cheap but not free, and every board on the page asks for them.
 */
let gamesCache: { v: number; games: GameRecord[] } | null = null;
function games(): GameRecord[] {
  const v = pairingVersion();
  if (!gamesCache || gamesCache.v !== v) gamesCache = { v, games: allGames() };
  return gamesCache.games;
}

/* -- one game at a time ------------------------------------------------------------------------ */

export interface GameRow {
  season: number;
  week: number;
  kind: string;
  /** Home and away as the archive recorded them, not sorted by result. */
  home: { gm: string | null; team: string; points: number };
  away: { gm: string | null; team: string; points: number };
  margin: number;
  total: number;
  winner: 'home' | 'away' | 'tie';
}

export interface GameFilter extends Scope {
  week?: number | 'all';
  kind?: 'all' | 'regular' | 'postseason';
  /** Matches a manager or a team name on either side. */
  q?: string;
}

/**
 * Every game, as a game rather than as a record.
 *
 * The leaderboards answer "what is the most/least ever", which is the fun question. This answers
 * the boring one that comes straight after it — "hang on, what actually happened that week?" —
 * and it is the only view in the app where a season with unresolved managers is still worth
 * showing, because the score is known even when the man is not.
 */
export function allGameRows(f: GameFilter = {}): GameRow[] {
  const { season: inSeason, gm: inGm } = withScope(f);
  const q = (f.q ?? '').trim().toLowerCase();
  const out: GameRow[] = [];

  for (const s of MATCHUPS) {
    if (!inSeason(s.season)) continue;
    for (const m of s.matchups) {
      if (f.week && f.week !== 'all' && m.week !== f.week) continue;
      const kind = m.kind ?? 'regular';
      if (f.kind === 'regular' && kind !== 'regular') continue;
      if (f.kind === 'postseason' && kind === 'regular') continue;

      const side = (x: Side) => ({ gm: gmFor(s.season, x), team: x.team, points: x.points });
      const home = side(m.home);
      const away = side(m.away);

      // A scoped board is about particular managers; a game counts if either side is one of them.
      const named = [home.gm, away.gm].filter((g): g is string => Boolean(g));
      if (named.length > 0 && !named.some(inGm)) continue;

      if (q && ![home, away].some((x) => (x.gm ?? '').toLowerCase().includes(q)
        || x.team.toLowerCase().includes(q))) continue;

      out.push({
        season: s.season,
        week: m.week,
        kind,
        home,
        away,
        margin: round(Math.abs(home.points - away.points)),
        total: round(home.points + away.points),
        winner: home.points > away.points ? 'home' : home.points < away.points ? 'away' : 'tie',
      });
    }
  }
  return out.sort((a, b) => b.season - a.season || b.week - a.week
    || b.total - a.total);
}

/** Weeks that exist at all, for the filter. */
export function gameWeeks(): number[] {
  return [...new Set(MATCHUPS.flatMap((s) => s.matchups.map((m) => m.week)))].sort((a, b) => a - b);
}

export const GAME_SEASONS: number[] = MATCHUPS.map((s) => s.season).sort((a, b) => b - a);

/**
 * What a team actually did in one season.
 *
 * For the handful of team names nobody can put a manager to, this is the jog: "THE GREEN LANTERN"
 * means nothing to anybody, but "8-6, 7th of ten, missed the playoffs" is the season somebody
 * remembers having. Looked up by team name because that is all those rows have.
 */
export interface TeamSeason {
  w: number; l: number; t: number; rank: number; of: number; pf: number; playoff: boolean;
  /** 'champion' | 'runner-up' | 'third' where the season recorded one. */
  finish: string | null;
}

export function teamRecord(season: number, team: string): TeamSeason | null {
  const norm = (x: string) => x.replace(/\s+/g, ' ').trim().toLowerCase();
  const s = STANDINGS.find((x) => x.season === season);
  if (!s) return null;
  const row = s.standings.find((r) => norm(r.team) === norm(team));
  if (!row) return null;
  const same = (label?: string) => Boolean(label && norm(label) === norm(team));
  return {
    w: row.w,
    l: row.l,
    t: row.t,
    rank: row.rank,
    of: s.standings.length,
    pf: row.pf,
    playoff: row.playoff,
    finish: same(s.champion) ? 'champion'
      : same(s.runnerUp) ? 'runner-up'
        : same(s.thirdPlace) ? 'third' : null,
  };
}

/* -- career records -------------------------------------------------------------------------- */

export interface CareerRow {
  gm: string;
  seasons: number;
  w: number; l: number; t: number;
  pf: number; pa: number;
  /** Games played, so a GM who joined in 2019 is legible next to one who has been here since 2009. */
  games: number;
  winPct: number;
  ppg: number;
  championships: number;
  runnerUps: number;
  playoffs: number;
  bestFinish: number;
}

export function careerTable(scope: Scope = {}): { rows: CareerRow[]; coverage: Coverage } {
  const { season: inSeason, gm: inGm } = withScope(scope);
  const acc = new Map<string, CareerRow>();
  const get = (gm: string) => {
    let r = acc.get(gm);
    if (!r) {
      r = {
        gm, seasons: 0, w: 0, l: 0, t: 0, pf: 0, pa: 0, games: 0, winPct: 0, ppg: 0,
        championships: 0, runnerUps: 0, playoffs: 0, bestFinish: 99,
      };
      acc.set(gm, r);
    }
    return r;
  };

  const used: number[] = [];
  for (const s of STANDINGS) {
    if (!inSeason(s.season)) continue;
    /*
     * A TEAM NOBODY HAS NAMED IS STILL SOMEBODY (Tim, 8 September: "pretend the unpaired 2003-2006
     * identities are standalone identities carrying their own team name, likely never to be
     * paired").
     *
     * This used to skip any season holding an unresolved row, which meant it skipped 2003 through
     * 2006 whole — and since the archive's first four seasons will never be paired, every career
     * total in the app was waiting on a repair that is not coming. `identityFor` never returns
     * null: an unpaired team is its own manager, so the ten men who played against him get those
     * four seasons back and he gets a line of his own.
     */
    const resolved = s.standings.map((row) => ({ row, gm: identityFor(s.season, row) }));
    used.push(s.season);
    for (const { row, gm } of resolved) {
      if (!inGm(gm!)) continue;
      const r = get(gm!);
      r.seasons += 1;
      r.w += row.w; r.l += row.l; r.t += row.t;
      r.pf += row.pf; r.pa += row.pa;
      r.games += row.w + row.l + row.t;
      if (row.playoff) r.playoffs += 1;
      r.bestFinish = Math.min(r.bestFinish, row.rank);
    }
    // The champion fields hold whatever the season's rows held — a manager from 2009 on, a TEAM
    // NAME before that. Resolving them with the generic path treated "The Insane Israeli" as a
    // person and gave a team its own line in the career table. Resolve them the way their own
    // standings row resolves instead, which is the only reading that is right in both eras.
    const winner = (label: string | undefined): string | null => {
      if (!label) return null;
      const row = resolved.find((x) => x.row.team === label || x.row.gm === label);
      return row ? row.gm : null;
    };
    const champ = winner(s.champion);
    const second = winner(s.runnerUp);
    if (champ && inGm(champ)) get(champ).championships += 1;
    if (second && inGm(second)) get(second).runnerUps += 1;
  }

  const rows = [...acc.values()].map((r) => ({
    ...r,
    pf: round(r.pf),
    pa: round(r.pa),
    winPct: r.games ? (r.w + r.t / 2) / r.games : 0,
    ppg: r.games ? round(r.pf / r.games) : 0,
  })).sort((a, b) => b.winPct - a.winPct || b.pf - a.pf);

  /*
   * NOTHING IS EXCLUDED FROM THIS ONE ANY MORE. `unnamed()` is a statement about the MATCHUP
   * archive — the seasons Yahoo kept no manager names for — and it belongs on the boards that read
   * matchups. This board reads standings, and every standings season is counted now that an
   * unpaired team is its own manager rather than a hole in somebody else's record.
   */
  return { rows, coverage: coverageOf(used) };
}

/* -- head to head ---------------------------------------------------------------------------- */

export interface H2HCell {
  w: number; l: number; t: number; pf: number; pa: number;
  /** Ties count as half a win, the way the league's own standings do. */
  pct: number;
  games: number;
}

export function headToHead(scope: Scope = {}): {
  gms: string[]; grid: Map<string, Map<string, H2HCell>>; coverage: Coverage;
} {
  const { season: inSeason, gm: inGm } = withScope(scope);
  const grid = new Map<string, Map<string, H2HCell>>();
  const used = new Set<number>();

  for (const g of games()) {
    if (!g.opponentResolved || g.opponent === g.gm) continue;
    if (!inSeason(g.season) || !inGm(g.gm) || !inGm(g.opponent)) continue;
    used.add(g.season);
    const row = grid.get(g.gm) ?? new Map<string, H2HCell>();
    const cell = row.get(g.opponent) ?? { w: 0, l: 0, t: 0, pf: 0, pa: 0, pct: 0, games: 0 };
    if (g.points > g.opponentPoints) cell.w += 1;
    else if (g.points < g.opponentPoints) cell.l += 1;
    else cell.t += 1;
    cell.pf = round(cell.pf + g.points);
    cell.pa = round(cell.pa + g.opponentPoints);
    cell.games = cell.w + cell.l + cell.t;
    cell.pct = cell.games ? (cell.w + cell.t / 2) / cell.games : 0;
    row.set(g.opponent, cell);
    grid.set(g.gm, row);
  }

  const gms = [...grid.keys()].sort();
  return { gms, grid, coverage: coverageOf([...used], unnamed().filter((u) => inSeason(u.season))) };
}

/* -- leaderboards ---------------------------------------------------------------------------- */

export interface Leader {
  label: string;
  gm: string;
  value: string;
  detail: string;
  /**
   * The game this record was set in, where the record IS a game.
   *
   * "Narrowest win, 0.12" is a fact nobody believes until they see the two lineups that produced
   * it, and the follow-up is always the same — who did that, and what was on the bench. Season
   * records and keeper records have no single game behind them and leave this unset, which is
   * what the view keys expandability off rather than guessing from the title.
   */
  game?: GameRef;
}

/** Enough to find the game again: the box scores are filed under season, week and team name. */
export interface GameRef { season: number; week: number; team: string }

export interface Leaderboard {
  title: string;
  note?: string;
  coverage: Coverage;
  entries: Leader[];
}

const fmt = (n: number) => n.toFixed(2);

/**
 * The opponent, as a person where there is one.
 *
 * `opponent` holds a GM id from 2009 on and a raw team name before that. Printing the id gives
 * "over ARI", which is how the app ends up shouting surnames at people; printing the team name
 * unchanged is right, because in 2005 a team name is all anybody has.
 */
const opp = (g: GameRecord) => (g.opponentResolved ? managerShort(g.opponent) : g.opponent);
const round = (n: number) => Math.round(n * 100) / 100;

const gameCoverage = (sc: Required<Scope>) => coverageOf(
  [...new Set(games().filter((g) => sc.season(g.season) && sc.gm(g.gm)).map((g) => g.season))],
  unnamed().filter((u) => sc.season(u.season)),
);

/**
 * `rank` is what the board sorts on; `show` is what it prints. They differ for "narrowest win",
 * where the sort wants the negated margin and the reader wants the margin — printing the sort key
 * put a minus sign in front of every entry.
 */
/** Ten is the league's unit — ten teams, top tens. Five felt arbitrary and cut off arguments. */
const TOP_N = 10;

function topGames(
  sc: Required<Scope>,
  title: string,
  rank: (g: GameRecord) => number,
  describe: (g: GameRecord) => string,
  n = TOP_N,
  filter: (g: GameRecord) => boolean = () => true,
  worst = false,
  show: (g: GameRecord) => number = rank,
): Leaderboard {
  const sorted = games()
    .filter((g) => sc.season(g.season) && sc.gm(g.gm))
    .filter(filter)
    .sort((a, b) => (worst ? rank(a) - rank(b) : rank(b) - rank(a)))
    .slice(0, n);
  return {
    title,
    coverage: gameCoverage(sc),
    entries: sorted.map((g) => ({
      label: `${g.season} wk ${g.week}`,
      gm: g.gm,
      value: fmt(show(g)),
      detail: describe(g),
      game: { season: g.season, week: g.week, team: g.team },
    })),
  };
}

/**
 * The game behind a record, as a row the game views already know how to draw.
 *
 * Looked up by team name rather than by manager, because that is the one key that works in every
 * season — before 2009 Yahoo recorded no manager at all, and a record set in 2005 should still
 * open.
 */
export function gameRowFor(ref: GameRef): GameRow | null {
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  const want = norm(ref.team);
  for (const s of MATCHUPS) {
    if (s.season !== ref.season) continue;
    for (const m of s.matchups) {
      if (m.week !== ref.week) continue;
      if (norm(m.home.team) !== want && norm(m.away.team) !== want) continue;
      const side = (x: Side) => ({ gm: gmFor(s.season, x), team: x.team, points: x.points });
      const home = side(m.home);
      const away = side(m.away);
      return {
        season: s.season,
        week: m.week,
        kind: m.kind ?? 'regular',
        home,
        away,
        margin: round(Math.abs(home.points - away.points)),
        total: round(home.points + away.points),
        winner: home.points > away.points ? 'home' : home.points < away.points ? 'away' : 'tie',
      };
    }
  }
  return null;
}

export function scoringRecords(scope: Scope = {}): Leaderboard[] {
  const sc = withScope(scope);
  return [
    topGames(
      sc,
      'Highest single week',
      (g) => g.points,
      (g) => `beat ${opp(g)} ${fmt(g.points)}–${fmt(g.opponentPoints)}`,
    ),
    topGames(
      sc,
      'Lowest single week',
      (g) => g.points,
      (g) => `vs ${opp(g)}, who scored ${fmt(g.opponentPoints)}`,
      TOP_N, () => true, true,
    ),
    topGames(
      sc,
      'Biggest blowout',
      (g) => g.points - g.opponentPoints,
      (g) => `${fmt(g.points)}–${fmt(g.opponentPoints)} over ${opp(g)}`,
    ),
    topGames(
      sc,
      'Narrowest win',
      (g) => -(g.points - g.opponentPoints),
      (g) => `${fmt(g.points)}–${fmt(g.opponentPoints)} over ${opp(g)}`,
      TOP_N,
      (g) => g.points > g.opponentPoints,
      false,
      (g) => g.points - g.opponentPoints,
    ),
    topGames(
      sc,
      'Most points in a loss',
      (g) => g.points,
      (g) => `lost to ${opp(g)}, who put up ${fmt(g.opponentPoints)}`,
      TOP_N,
      (g) => g.points < g.opponentPoints,
    ),
    topGames(
      sc,
      'Fewest points in a win',
      (g) => g.points,
      (g) => `beat ${opp(g)}, who managed ${fmt(g.opponentPoints)}`,
      TOP_N,
      (g) => g.points > g.opponentPoints,
      true,
    ),
  ];
}

export function seasonRecords(scope: Scope = {}): Leaderboard[] {
  const { season: inSeason, gm: inGm } = withScope(scope);
  const rows: Array<{ s: StandingSeason; r: StandingRow; gm: string }> = [];
  const used = new Set<number>();
  for (const s of STANDINGS) {
    if (!inSeason(s.season)) continue;
    for (const r of s.standings) {
      const gm = gmFor(s.season, r);
      if (!gm || !inGm(gm)) continue;
      used.add(s.season);
      rows.push({ s, r, gm });
    }
  }
  const cov = coverageOf([...used], unnamed().filter((u) => inSeason(u.season)));
  const board = (
    title: string,
    pick: (x: { s: StandingSeason; r: StandingRow; gm: string }) => number,
    detail: (x: { s: StandingSeason; r: StandingRow; gm: string }) => string,
    worst = false,
    note?: string,
  ): Leaderboard => ({
    title,
    ...(note ? { note } : {}),
    coverage: cov,
    entries: [...rows]
      .sort((a, b) => (worst ? pick(a) - pick(b) : pick(b) - pick(a)))
      .slice(0, TOP_N)
      .map((x) => ({
        label: String(x.s.season), gm: x.gm, value: fmt(pick(x)), detail: detail(x),
      })),
  });

  return [
    board('Most points, season', (x) => x.r.pf, (x) => `${x.r.w}-${x.r.l}, finished ${ordinal(x.r.rank)}`),
    board('Fewest points, season', (x) => x.r.pf, (x) => `${x.r.w}-${x.r.l}, finished ${ordinal(x.r.rank)}`, true),
    board(
      'Most points against',
      (x) => x.r.pa,
      (x) => `${x.r.w}-${x.r.l} — scored ${fmt(x.r.pf)} himself`,
      false,
      'The unluckiest seasons in the league, or the best schedule of opponents.',
    ),
    board(
      'Best points-for, worst finish',
      (x) => x.r.pf / 100 + x.r.rank,
      (x) => `${fmt(x.r.pf)} points and still finished ${ordinal(x.r.rank)}`,
      false,
      'Scoring a lot and finishing badly. The number is a blend, not a stat — it is here to find the season somebody deserved better.',
    ),
  ];
}

/* -- draft and keepers ------------------------------------------------------------------------ */

interface KeeperSeason { season: number; keepers: Array<{ gm: string; player: string; round: number }> }
interface DraftSeason {
  season: number;
  picks: Array<{ gm: string; player: string; round: number; keeper?: boolean; gmResolved?: boolean }>;
}

const KEEPERS = (keeperData as unknown as { seasons: KeeperSeason[] }).seasons;
const DRAFTS = (draftData as unknown as { seasons: DraftSeason[] }).seasons;

export function keeperRecords(scope: Scope = {}): Leaderboard[] {
  const { season: inSeason, gm: inGm } = withScope(scope);
  const keep = (season: number, gm: string) => inSeason(season)
    && inGm(gmFor(season, { gm, team: gm }) ?? gm);
  const runs = new Map<string, { gms: Set<string>; years: number[] }>();
  for (const s of KEEPERS) {
    for (const k of s.keepers) {
      if (!keep(s.season, k.gm)) continue;
      const r = runs.get(k.player) ?? { gms: new Set<string>(), years: [] };
      r.gms.add(gmFor(s.season, { gm: k.gm, team: k.gm }) ?? k.gm);
      r.years.push(s.season);
      runs.set(k.player, r);
    }
  }
  const cov = coverageOf(KEEPERS.map((s) => s.season).filter(inSeason));

  const longest: Leaderboard = {
    title: 'Longest keeper runs',
    note: 'Keepers are marked from 2010 — Yahoo\'s own K icon for 2010-2015, the Draftboard '
      + 'sheet from 2016. Before 2010 neither source marks them, so a run that began earlier is '
      + 'a floor rather than a count.',
    coverage: cov,
    entries: [...runs.entries()]
      .sort((a, b) => b[1].years.length - a[1].years.length || a[0].localeCompare(b[0]))
      .slice(0, TOP_N)
      .map(([player, r]) => ({
        label: player,
        gm: [...r.gms].join(' → '),
        value: String(r.years.length),
        detail: `${Math.min(...r.years)}–${Math.max(...r.years)}`,
      })),
  };

  const byGmSeason = new Map<string, number>();
  for (const s of KEEPERS) {
    for (const k of s.keepers) {
      if (!keep(s.season, k.gm)) continue;
      const key = `${gmFor(s.season, { gm: k.gm, team: k.gm }) ?? k.gm}|${s.season}`;
      byGmSeason.set(key, (byGmSeason.get(key) ?? 0) + 1);
    }
  }
  const most: Leaderboard = {
    title: 'Most keepers declared',
    coverage: cov,
    entries: [...byGmSeason.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, TOP_N)
      .map(([key, n]) => {
        const [gm, season] = key.split('|');
        return { label: season!, gm: gm!, value: String(n), detail: 'in one season' };
      }),
  };

  return [longest, most];
}

export function draftRecords(scope: Scope = {}): Leaderboard[] {
  const { season: inSeason, gm: inGm } = withScope(scope);
  const picked = new Map<string, { n: number; gms: Set<string>; years: number[] }>();
  const byGm = new Map<string, number>();
  const used: number[] = [];
  for (const s of DRAFTS) {
    if (!inSeason(s.season)) continue;
    used.push(s.season);
    for (const p of s.picks) {
      if (!p.player) continue;
      const r = picked.get(p.player) ?? { n: 0, gms: new Set<string>(), years: [] };
      r.n += 1;
      // Draft results shout the team name into the gm field when Yahoo hid the manager, so
      // the overlay is what turns "WAIVER WIRE GOONS" back into a person.
      const gm = gmFor(s.season, { gm: p.gm, team: p.gm, ...(p.gmResolved === undefined ? {} : { gmResolved: p.gmResolved }) });
      if (gm && !inGm(gm)) continue;
      if (gm) r.gms.add(gm);
      r.years.push(s.season);
      picked.set(p.player, r);
      if (gm) byGm.set(gm, (byGm.get(gm) ?? 0) + 1);
    }
  }
  const cov = coverageOf(used);

  return [
    {
      title: 'Most drafted players',
      note: 'How many times a name has been called across 23 drafts.',
      coverage: cov,
      entries: [...picked.entries()]
        .sort((a, b) => b[1].n - a[1].n || a[0].localeCompare(b[0]))
        .slice(0, TOP_N)
        .map(([player, r]) => ({
          label: player,
          gm: `${r.gms.size} different GM${r.gms.size === 1 ? '' : 's'}`,
          value: String(r.n),
          detail: `${Math.min(...r.years)}–${Math.max(...r.years)}`,
        })),
    },
  ];
}

/* -- the toilet bowl and the podium ------------------------------------------------------------ */

export interface FinishRow { gm: string; firsts: number; seconds: number; thirds: number; lasts: number; toilets: number }

export function finishes(scope: Scope = {}): { rows: FinishRow[]; coverage: Coverage } {
  const { season: inSeason, gm: inGm } = withScope(scope);
  const acc = new Map<string, FinishRow>();
  const get = (gm: string) => {
    let r = acc.get(gm);
    if (!r) { r = { gm, firsts: 0, seconds: 0, thirds: 0, lasts: 0, toilets: 0 }; acc.set(gm, r); }
    return r;
  };
  const used: number[] = [];
  for (const s of STANDINGS) {
    if (!inSeason(s.season)) continue;
    /* Same reading as `careerTable`: an unpaired team is its own manager, never a skipped season. */
    const resolved = s.standings.map((r) => ({ r, gm: identityFor(s.season, r) }));
    used.push(s.season);
    for (const { r, gm } of resolved) {
      if (!inGm(gm!)) continue;
      const row = get(gm!);
      if (r.rank === 1) row.firsts += 1;
      if (r.rank === 2) row.seconds += 1;
      if (r.rank === 3) row.thirds += 1;
      if (r.rank === s.teams) row.lasts += 1;
      // Yahoo ranks the consolation-bracket winner immediately below the playoff field.
      const playoffTeams = s.standings.filter((x) => x.playoff).length;
      if (playoffTeams > 0 && r.rank === playoffTeams + 1) row.toilets += 1;
    }
  }
  const rows = [...acc.values()].sort(
    (a, b) => b.firsts - a.firsts || b.seconds - a.seconds || b.thirds - a.thirds,
  );
  /*
   * NOTHING IS EXCLUDED FROM THIS ONE ANY MORE. `unnamed()` is a statement about the MATCHUP
   * archive — the seasons Yahoo kept no manager names for — and it belongs on the boards that read
   * matchups. This board reads standings, and every standings season is counted now that an
   * unpaired team is its own manager rather than a hole in somebody else's record.
   */
  return { rows, coverage: coverageOf(used) };
}

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  const suffix = s[(v - 20) % 10] ?? s[v] ?? 'th';
  return `${n}${suffix}`;
};


/* -- reading the head-to-head grid ------------------------------------------------------------
 *
 * Two things the grid is asked and could not answer at a glance: who does this man beat up on,
 * and who owns him. The numbers are all there; twenty-four of them in a row is not a reading.
 */

/**
 * The best and worst matchups in ONE ROW, among the opponents currently visible.
 *
 * "Currently visible" is the filter doing its job: with `Current managers` selected, a GM's best
 * record should be his best against the people still in the league, not against somebody who left
 * in 2008. Ties are all marked — two opponents at 75% are both his best, and picking one of them
 * arbitrarily would be a lie the grid tells silently.
 *
 * Nothing is marked when there is nothing to compare: a row with one played opponent has no best
 * and no worst, and a row where every record is identical has no extremes either.
 */
export function h2hExtremes(
  row: ReadonlyMap<string, H2HCell> | undefined,
  gms: readonly string[],
  self: string,
): { best: Set<string>; worst: Set<string> } {
  const empty = { best: new Set<string>(), worst: new Set<string>() };
  if (!row) return empty;

  const played = gms
    .filter((g) => g.toUpperCase() !== self.toUpperCase())
    .map((g) => ({ gm: g, cell: row.get(g) }))
    .filter((x): x is { gm: string; cell: H2HCell } => Boolean(x.cell && x.cell.games > 0));

  if (played.length < 2) return empty;

  const hi = Math.max(...played.map((x) => x.cell.pct));
  const lo = Math.min(...played.map((x) => x.cell.pct));
  if (hi === lo) return empty;

  return {
    best: new Set(played.filter((x) => x.cell.pct === hi).map((x) => x.gm)),
    worst: new Set(played.filter((x) => x.cell.pct === lo).map((x) => x.gm)),
  };
}

export type H2HSort = 'manager' | 'total';

/** A GM's record against everyone visible — the grid's Total column. */
export function h2hTotal(
  row: ReadonlyMap<string, H2HCell> | undefined,
  gms: readonly string[],
  self: string,
): { w: number; l: number; t: number; pct: number } {
  let w = 0, l = 0, t = 0;
  for (const g of gms) {
    if (g.toUpperCase() === self.toUpperCase()) continue;
    const c = row?.get(g);
    if (!c) continue;
    w += c.w; l += c.l; t += c.t;
  }
  const games = w + l + t;
  return { w, l, t, pct: games ? (w + t / 2) / games : 0 };
}

/**
 * Row and column order together.
 *
 * Deliberately ONE order for both. Sorting the rows alone would scatter the self-diagonal across
 * the grid, and a matrix whose diagonal wanders is unreadable however correct each cell is.
 */
export function h2hOrder(
  gms: readonly string[],
  grid: ReadonlyMap<string, ReadonlyMap<string, H2HCell>>,
  sort: H2HSort,
  desc: boolean,
): string[] {
  const out = [...gms];
  if (sort === 'manager') {
    out.sort((a, b) => a.localeCompare(b));
  } else {
    out.sort((a, b) => {
      const ta = h2hTotal(grid.get(a), gms, a);
      const tb = h2hTotal(grid.get(b), gms, b);
      return tb.pct - ta.pct || (tb.w - tb.l) - (ta.w - ta.l) || a.localeCompare(b);
    });
  }
  return desc ? out : out.reverse();
}

export { ordinal };
